/**
 * Contact form component for Stack Forward website
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { validateContactForm, sanitizeInput } from '../lib/validators';
import {
  submitContact,
  extractUTMParams,
  fetchInterestOptions,
} from '../lib/pb';
import type { InterestOption } from '../lib/pb';
import type { ContactFormData, FormState } from '../types';
import { InterestSelector } from './InterestSelector';

/**
 * Contact form component with validation and PocketBase integration
 */
export const ContactForm: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: [],
    message: '',
    consent: false,
  });

  const [formState, setFormState] = useState<FormState>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');
  const [interestOptions, setInterestOptions] = useState<InterestOption[]>([]);

  // Fetch interest options on mount
  useEffect(() => {
    fetchInterestOptions().then(setInterestOptions);
  }, []);

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Don't sanitize during typing - only store the raw value
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle input blur (sanitize on blur)
  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name !== 'message') {
      // Don't sanitize message field
      const sanitizedValue = sanitizeInput(value);
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    }
  };

  // Handle honeypot change (should remain empty)
  const handleHoneypotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoneypot(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate honeypot
    if (honeypot.trim() !== '') {
      console.warn('Honeypot filled - submission blocked');
      return;
    }

    // Validate form data
    const validation = validateContactForm(formData);

    if (!validation.success) {
      const newErrors: Record<string, string> = {};
      validation.error.errors.forEach(error => {
        const field = error.path[0] as string;
        newErrors[field] = error.message;
      });
      setErrors(newErrors);
      toast.error('Please fix the errors below');
      return;
    }

    // Submit form
    setFormState('submitting');

    try {
      const utmParams = extractUTMParams();

      await submitContact({
        ...formData,
        utm: Object.keys(utmParams).length > 0 ? utmParams : undefined,
        __hp: honeypot,
      });

      // Success
      setFormState('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: [],
        message: '',
        consent: false,
      });
      setHoneypot('');
      setErrors({});

      toast.success("Thank you! We'll get back to you within 24 hours.");
    } catch (error) {
      setFormState('error');
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
    }
  };

  // Success state
  if (formState === 'success') {
    return (
      <section id="contact" className="section bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-2xl mb-6 mx-auto">
                ✅
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-4">
                Thank You for Your Interest!
              </h2>
              <p className="text-green-700 mb-6">
                We've received your message and will get back to you within 24
                hours. In the meantime, feel free to reach out to us directly at{' '}
                <a
                  href="mailto:hello@stackforward.org"
                  className="font-semibold underline"
                >
                  hello@stackforward.org
                </a>
              </p>
              <button
                onClick={() => setFormState('idle')}
                className="btn-primary"
              >
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="section bg-neutral-50">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Get Your Free Technical Assessment
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Ready to transform your technology stack? Let's discuss your
              requirements and create a custom roadmap for your open-source
              journey.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
                  Why Choose Stack Forward?
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-semibold mt-0.5">
                      1
                    </span>
                    <div>
                      <h4 className="font-semibold text-neutral-900">
                        Free Technical Assessment
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        30-minute consultation to understand your needs
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-semibold mt-0.5">
                      2
                    </span>
                    <div>
                      <h4 className="font-semibold text-neutral-900">
                        Custom Implementation Plan
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        Tailored roadmap for your specific requirements
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-semibold mt-0.5">
                      3
                    </span>
                    <div>
                      <h4 className="font-semibold text-neutral-900">
                        Ongoing Support
                      </h4>
                      <p className="text-neutral-600 text-sm">
                        24/7 support and maintenance services
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 border border-neutral-200">
                <h4 className="font-semibold text-neutral-900 mb-3">
                  Direct Contact
                </h4>
                <p className="text-neutral-600 text-sm mb-4">
                  Prefer to talk directly? We're here to help.
                </p>
                <a
                  href="mailto:hello@stackforward.org"
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  hello@stackforward.org
                </a>
              </div>
            </div>

            {/* Contact form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="form-label form-label-required"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                    placeholder="Enter your full name"
                    disabled={formState === 'submitting'}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <p
                      id="name-error"
                      className="text-red-600 text-sm mt-1"
                      role="alert"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="form-label form-label-required"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                    placeholder="Enter your email address"
                    disabled={formState === 'submitting'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p
                      id="email-error"
                      className="text-red-600 text-sm mt-1"
                      role="alert"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`form-input ${errors.phone ? 'form-input-error' : ''}`}
                    placeholder="Enter your phone number (optional)"
                    disabled={formState === 'submitting'}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                  />
                  {errors.phone && (
                    <p
                      id="phone-error"
                      className="text-red-600 text-sm mt-1"
                      role="alert"
                    >
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="form-label">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`form-input ${errors.company ? 'form-input-error' : ''}`}
                    placeholder="Enter your company name (optional)"
                    disabled={formState === 'submitting'}
                    aria-describedby={
                      errors.company ? 'company-error' : undefined
                    }
                  />
                  {errors.company && (
                    <p
                      id="company-error"
                      className="text-red-600 text-sm mt-1"
                      role="alert"
                    >
                      {errors.company}
                    </p>
                  )}
                </div>

                {/* Interest */}
                <div>
                  <label className="form-label form-label-required">
                    Areas of Interest
                  </label>
                  {interestOptions.length > 0 ? (
                    <InterestSelector
                      options={interestOptions}
                      selected={formData.interest}
                      onChange={selected => {
                        setFormData(prev => ({ ...prev, interest: selected }));
                        // Clear error when user makes selection
                        if (errors.interest) {
                          setErrors(prev => ({ ...prev, interest: '' }));
                        }
                      }}
                      disabled={formState === 'submitting'}
                      error={errors.interest}
                    />
                  ) : (
                    <div className="px-4 py-3 border border-neutral-300 rounded-lg bg-neutral-50">
                      <span className="text-sm text-neutral-500">
                        Loading options...
                      </span>
                    </div>
                  )}
                  {errors.interest && !interestOptions.length && (
                    <p className="text-red-600 text-sm mt-1" role="alert">
                      {errors.interest}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="form-label form-label-required"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    rows={5}
                    className={`form-input ${errors.message ? 'form-input-error' : ''}`}
                    placeholder="Tell us about your project requirements, current challenges, or any questions you have..."
                    disabled={formState === 'submitting'}
                    aria-describedby={
                      errors.message ? 'message-error' : undefined
                    }
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.message ? (
                      <p
                        id="message-error"
                        className="text-red-600 text-sm"
                        role="alert"
                      >
                        {errors.message}
                      </p>
                    ) : (
                      <div></div>
                    )}
                    <p className="text-neutral-500 text-sm">
                      {formData.message.length}/2000 characters
                    </p>
                  </div>
                </div>

                {/* Consent */}
                <div>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      disabled={formState === 'submitting'}
                      className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500 mt-1"
                    />
                    <span className="text-sm text-neutral-700">
                      I agree to the{' '}
                      <Link
                        to="/privacy"
                        className="text-primary-600 hover:text-primary-700 underline"
                      >
                        Privacy Policy
                      </Link>{' '}
                      and consent to Stack Forward using my information to
                      respond to my inquiry.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="text-red-600 text-sm mt-1" role="alert">
                      {errors.consent}
                    </p>
                  )}
                </div>

                {/* Honeypot */}
                <div style={{ display: 'none' }} aria-hidden="true">
                  <label htmlFor="__hp">Leave this field empty</label>
                  <input
                    type="text"
                    id="__hp"
                    name="__hp"
                    value={honeypot}
                    onChange={handleHoneypotChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formState === 'submitting' ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>

                {/* Privacy note */}
                <p className="text-xs text-neutral-500 text-center">
                  We'll use your information to respond to your inquiry. No
                  spam, ever.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
